import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'

type CursorMode = 'default' | 'link' | 'view' | 'drag' | 'text' | 'hidden'

const SIZES: Record<CursorMode, number> = {
    default: 22,
    link: 40,
    view: 22,
    drag: 92,
    text: 10,
    hidden: 10
}

const isCursorMode = (value: string | undefined): value is CursorMode => {
    return value === 'default' || value === 'link' || value === 'view' || value === 'drag' || value === 'text' || value === 'hidden'
}

const CORNERS = [
    'left-0 top-0 border-l border-t',
    'right-0 top-0 border-r border-t',
    'bottom-0 left-0 border-b border-l',
    'bottom-0 right-0 border-b border-r'
]

export const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const [mode, setMode] = useState<CursorMode>('hidden')
    const [label, setLabel] = useState('')
    const pointer = useRef({ x: -100, y: -100 })

    const x = useMotionValue(-100)
    const y = useMotionValue(-100)
    const frameX = useSpring(x, { stiffness: 520, damping: 42, mass: 0.55 })
    const frameY = useSpring(y, { stiffness: 520, damping: 42, mass: 0.55 })
    const focusX = useSpring(x, { stiffness: 340, damping: 34, mass: 0.7 })
    const focusY = useSpring(y, { stiffness: 340, damping: 34, mass: 0.7 })

    useEffect(() => {
        const media = window.matchMedia('(hover: hover) and (pointer: fine)')
        const update = () => setEnabled(media.matches)

        update()
        media.addEventListener('change', update)

        return () => media.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        if (!enabled) return

        const root = document.documentElement
        let frame = 0

        root.classList.add('has-reticle')

        const resolve = (target: Element | null) => {
            if (!target) return

            const custom = target.closest<HTMLElement>('[data-cursor]')

            if (custom && isCursorMode(custom.dataset.cursor)) {
                setMode(custom.dataset.cursor)
                setLabel(custom.dataset.cursorLabel ?? '')
                return
            }

            if (target.closest('input, textarea, select, [contenteditable="true"]')) {
                setMode('text')
                setLabel('')
                return
            }

            if (target.closest('a, button, [role="button"], label, summary')) {
                setMode('link')
                setLabel('')
                return
            }

            setMode('default')
            setLabel('')
        }

        const onMove = (event: PointerEvent) => {
            pointer.current = { x: event.clientX, y: event.clientY }
            x.set(event.clientX)
            y.set(event.clientY)
            resolve(event.target as Element | null)
        }

        const onScroll = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                resolve(document.elementFromPoint(pointer.current.x, pointer.current.y))
            })
        }

        const onLeave = () => setMode('hidden')

        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('scroll', onScroll, { passive: true })
        document.documentElement.addEventListener('mouseleave', onLeave)

        return () => {
            cancelAnimationFrame(frame)
            root.classList.remove('has-reticle')
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('scroll', onScroll)
            document.documentElement.removeEventListener('mouseleave', onLeave)
        }
    }, [enabled, x, y])

    if (!enabled) return null

    const focusing = mode === 'view'
    const blended = mode === 'default' || mode === 'link' || mode === 'drag'

    return (
        <>
            <motion.div
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-[130] mix-blend-difference'
                style={{ x: frameX, y: frameY }}
            >
                <motion.div
                    className='relative -translate-x-1/2 -translate-y-1/2'
                    initial={false}
                    animate={{
                        width: SIZES[mode],
                        height: SIZES[mode],
                        opacity: blended ? 1 : 0,
                        rotate: mode === 'link' ? 45 : 0
                    }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.6 }}
                >
                    {CORNERS.map(position => (
                        <span key={position} className={cn('absolute size-2.5 border-bone', position)} />
                    ))}

                    {mode === 'drag' && (
                        <span className='absolute inset-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.22em] text-bone'>
                            <span className='mr-2'>&larr;</span>
                            {label || 'Drag'}
                            <span className='ml-2'>&rarr;</span>
                        </span>
                    )}
                </motion.div>
            </motion.div>

            <motion.div
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-[131]'
                style={{ x: focusX, y: focusY }}
            >
                <AnimatePresence>
                    {focusing && (
                        <motion.div
                            key={`focus-${label}`}
                            className='absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2'
                            initial={{ width: 168, height: 168, opacity: 0 }}
                            animate={{ width: 84, height: 84, opacity: 1 }}
                            exit={{ width: 120, height: 120, opacity: 0, transition: { duration: 0.2 } }}
                            transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                        >
                            {CORNERS.map(position => (
                                <motion.span
                                    key={position}
                                    className={cn('absolute size-4 border-[1.5px]', position)}
                                    initial={{ borderColor: '#ece6da' }}
                                    animate={{ borderColor: '#d4652f' }}
                                    transition={{ delay: 0.22, duration: 0.15 }}
                                />
                            ))}

                            <motion.span
                                className='absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ember'
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.8, 1] }}
                                transition={{ delay: 0.22, duration: 0.35 }}
                            />

                            <motion.span
                                className='absolute -top-5 left-0 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-bone'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.25 }}
                            >
                                <span className='size-1 rounded-full bg-ember' />
                                AF
                            </motion.span>

                            {label && (
                                <motion.span
                                    className='absolute left-1/2 top-[calc(100%+10px)] -translate-x-1/2 whitespace-nowrap bg-peat/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bone backdrop-blur-sm'
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.18, duration: 0.3 }}
                                >
                                    {label}
                                </motion.span>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <motion.div
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-[132]'
                style={{ x, y }}
            >
                <motion.span
                    className='block size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ember'
                    initial={false}
                    animate={{ scale: blended ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                />
            </motion.div>
        </>
    )
}
